import { Dispatch, FC, SetStateAction, useCallback, useContext } from "react";
import { Button, Paper, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { closestCenter, DndContext, DragEndEvent } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import __ from "src/utils/Translations";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import {
  absencesAbsencesConfigurationCustomFields,
  absencesAbsencesConfigurationWrite,
} from "src/utils/RoleBasedSecurity/permissions";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import CustomFieldForm from "src/Pages/Company/Absences/AbsenceType/AbsenceTypeForm/CustomFieldsConfigurator/CustomFieldForm";
import CustomFieldPreview from "src/Pages/Company/Absences/AbsenceType/AbsenceTypeForm/CustomFieldsConfigurator/CustomFieldPreview";
import { CustomFieldSchema } from "src/Pages/Company/Absences/AbsenceType/AbsenceTypeForm/CustomFieldsConfigurator/CustomFieldSchema";
import { ICustomField } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

interface ICustomFieldsConfiguratorProps {
  customFields: ICustomField[];
  setCustomFields: Dispatch<SetStateAction<ICustomField[]>>;
}

const CustomFieldsConfigurator: FC<ICustomFieldsConfiguratorProps> = ({
  customFields,
  setCustomFields,
}) => {
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const canWriteCustomFields = usePermission(
    absencesAbsencesConfigurationCustomFields
  );
  const { setModal, resetModal } = useContext(GlobalModalContext);

  const handleAddCustomField = useCallback(
    (field: CustomFieldSchema, editingFieldId: string | null) => {
      if (editingFieldId) {
        // Update existing field
        setCustomFields((prevFields) =>
          prevFields.map((f) =>
            f.id === editingFieldId ? { ...field, id: f.id } : f
          )
        );
      } else {
        // Add new field
        setCustomFields((prevFields) => [
          ...prevFields,
          { ...field, isNew: true, id: crypto.randomUUID() },
        ]);
      }

      resetModal();
    },
    [resetModal, setCustomFields]
  );

  const handleDeleteField = useCallback(
    (fieldId: string) => {
      if (typeof setModal === "function") {
        const fieldToDelete = customFields.find(
          (field) => field.id === fieldId
        );

        if (fieldToDelete) {
          setModal({
            open: true,
            title: (
              <>
                {__("Czy na pewno chcesz usunąć")}{" "}
                <span style={{ color: "#036BAE" }}>{fieldToDelete.name}</span>?
              </>
            ),
            actions: [
              {
                label: __("Anuluj"),
                variant: "outlined",
                callbackFn: () => {
                  resetModal();
                },
              },
              {
                label: __("Usuń"),
                color: "error",
                variant: "contained",
                callbackFn: () => {
                  setCustomFields((prevFields) =>
                    prevFields.filter((field) => field.id !== fieldId)
                  );
                  resetModal();
                },
              },
            ],
          });
        }
      }
    },
    [customFields, resetModal, setCustomFields, setModal]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active && over && active.id !== over.id) {
        const oldIndex = customFields.findIndex(
          (item) => item.id === active.id
        );
        const newIndex = customFields.findIndex((item) => item.id === over.id);

        const reorderedFiles = arrayMove(customFields, oldIndex, newIndex);

        setCustomFields(reorderedFiles);
      }
    },
    [customFields, setCustomFields]
  );

  const handleOpenCustomFieldForm = useCallback(
    (field: ICustomField | null = null) => {
      if (typeof setModal === "function") {
        setModal({
          open: true,
          title: null,
          customElements: (
            <>
              <Typography variant="subtitle1" component="h4" mb="1rem">
                {__("Dodatkowe pola")}
              </Typography>
              <CustomFieldForm
                field={field}
                fieldId={field?.id || null}
                handleAddCustomField={handleAddCustomField}
              />
            </>
          ),
          maxWidth: 1010,
          actions: null,
        });
      }
    },
    [handleAddCustomField, setModal]
  );

  const handleEditField = useCallback(
    (fieldId: string) => {
      const field = customFields.find((f) => f.id === fieldId);

      if (field) {
        handleOpenCustomFieldForm(field);
      }
    },
    [customFields, handleOpenCustomFieldForm]
  );

  return (
    <Paper
      sx={{ p: "1.5rem 2rem", mt: "1.5rem" }}
      data-testid="absence-type-custom-fields"
    >
      <Typography variant="subtitle1" component="h3" mb="1rem">
        {__("Dodatkowe pola")}
      </Typography>
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[
          restrictToVerticalAxis,
          restrictToWindowEdges,
          restrictToParentElement,
        ]}
      >
        <SortableContext
          items={customFields.map((field) => field.id)}
          strategy={verticalListSortingStrategy}
        >
          {customFields.map((field) => (
            <CustomFieldPreview
              key={field.id}
              field={field}
              disabled={!canWriteAbsencesConfiguration || !canWriteCustomFields}
              handleDeleteField={handleDeleteField}
              handleEditField={handleEditField}
            />
          ))}
        </SortableContext>
      </DndContext>
      {canWriteAbsencesConfiguration && canWriteCustomFields && (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => handleOpenCustomFieldForm(null)}
          data-testid="absence-type-custom-fields__button-add"
        >
          {__("Dodaj pole")}
        </Button>
      )}
    </Paper>
  );
};

export default CustomFieldsConfigurator;
